import type { WsMessage } from "@/api/websocket";
import { applyWorkToastQuestionDelivery, isWorkToastQuestionAnswered } from "./work-toast-question-delivery";
import {
  isAttentionState,
  isTerminalState,
} from "@/components/toasts/toast-status";
import type { FrameApplyContext } from "@/lib/companion-frame-reducer";
import { asActivityLabel } from "@/lib/activity-label";
import { stripCursorDisplayTokens } from "@/lib/cursor-token-display";
import {
  mergeWorkToastApps,
  parseWorkToastApps,
  parseWorkToastOpenTargets,
} from "@/lib/work-toast-apps";
import {
  createWorkToastItem,
  moveWorkToast,
  pushWorkToastActivity,
  newestWorkToast,
  payloadString,
  resolveWorkToastQuestion,
  workToastTaskTitle,
} from "@/lib/work-toast-item";
import { computerToastKey } from "@/lib/computer-toast-key";
import { adoptComputerToastIntoTask } from "@/lib/computer-use-toast-feed";
import { activityTextId, isCursorStatusContent, textActivityFromContent } from "@/lib/work-toast-tool-labels";
import {
  resultDetailLine,
  stripNativeResultPrefix,
  stripWorkerResultPrefix,
} from "@/lib/work-toast-result-summary";
import type {
  WorkToastItem,
  WorkToastQuestionOption,
  WorkToastSource,
  WorkToastState,
} from "@/stores/work-toast-store";

type Payload = Record<string, unknown>;

const TERMINAL_STATE: Record<string, WorkToastState> = {
  completed: "done",
  failed: "failed",
  cancelled: "stopped",
  paused: "stopped",
};

/** "Designer · WOR-3" → "Designer". */
function displayAgentName(name: string | null): string | null {
  return name?.split(" · ")[0]?.trim() || null;
}

function currentProjectId(ctx: FrameApplyContext): string | null {
  return ctx.projectStore.getState().currentProject?.id ?? null;
}

function payloadProjectId(data: Payload): string | null {
  return payloadString(data, "project_id") ?? payloadString(data, "space_id");
}

function applyRunning(
  data: Payload,
  ctx: FrameApplyContext,
  now: number,
): void {
  const key = payloadString(data, "execution_id");
  if (!key) return;
  const store = ctx.workToastStore.getState();
  const existing = store.items[key];
  const native = data.native_subagent === true;
  const taskId = payloadString(data, "task_id") ?? existing?.taskId ?? null;
  // The tray panel replaces the compact widget, so direct Denker runs without
  // a task id must be represented here as well. Previously the compact widget
  // owned that state and this adapter intentionally skipped it.
  const agentName = native
    ? "Denker"
    : (displayAgentName(payloadString(data, "agent_name")) ??
      existing?.agentName ??
      "Agent");
  const source: WorkToastSource = native ? "native_worker" : "agent";
  const prompt = payloadString(data, "prompt");
  const incomingApps = parseWorkToastApps(data);
  const mergedApps = incomingApps
    ? mergeWorkToastApps(existing?.apps ?? [], incomingApps)
    : (existing?.apps ?? []);
  const incomingTargets = parseWorkToastOpenTargets(data);
  const originDisplayId = typeof data.origin_display_id === "number" &&
    Number.isInteger(data.origin_display_id) && data.origin_display_id >= 0 && data.origin_display_id <= 0xffffffff
    ? data.origin_display_id
    : existing?.originDisplayId ?? null;
  const fields = {
    tracked: true,
    source,
    agentName,
    taskId,
    projectId:
      payloadProjectId(data) ?? existing?.projectId ?? currentProjectId(ctx),
    conversationId:
      payloadString(data, "conversation_id") ??
      existing?.conversationId ??
      null,
    originDisplayId,
    nativeSubagentId:
      payloadString(data, "native_subagent_id") ??
      existing?.nativeSubagentId ??
      null,
    apps: mergedApps,
    openTargets: incomingTargets ?? existing?.openTargets ?? [],
    detail:
      workToastTaskTitle(ctx, taskId) ??
      (prompt ? resultDetailLine(prompt) : null) ??
      (native ? payloadString(data, "agent_name") : null) ??
      existing?.detail ??
      "",
  } satisfies Partial<WorkToastItem>;
  if (!existing)
    store.upsertItem(
      createWorkToastItem({ key, state: "working", ...fields }, now),
    );
  else if (isAttentionState(existing.state)) store.patchItem(key, fields);
  else moveWorkToast(ctx, existing, "working", now, fields);
  // Desktop frames may have arrived first as a standalone computer toast for
  // the same execution: fold apps + screenshot + live activity into this
  // task card so computer-use never shows as a second card.
  adoptComputerToastIntoTask(key);
}

const ACCEPTED_LIVE_STATUSES = new Set(["queued", "running"]);

/** Shows the work card when the server accepts a prompt, so the user does not
 *  wait for the companion to start the run. The later `running` frame fills in
 *  the rest. A prompt joining a conversation that already shows a live card
 *  waits behind that run, so it gets no second card. */
function applyAcceptedPrompt(
  data: Payload,
  ctx: FrameApplyContext,
  now: number,
): void {
  const key = payloadString(data, "execution_id");
  if (
    data.accepted !== true ||
    !key ||
    !payloadString(data, "agent_name") ||
    payloadString(data, "steered_into_execution_id") ||
    !ACCEPTED_LIVE_STATUSES.has(String(data.execution_status))
  )
    return;
  const { items } = ctx.workToastStore.getState();
  if (items[key]) return;
  const conversationId = payloadString(data, "conversation_id");
  const conversationBusy =
    conversationId !== null &&
    Object.values(items).some(
      (item) =>
        item.conversationId === conversationId &&
        !item.dismissed &&
        !isTerminalState(item.state),
    );
  if (!conversationBusy) applyRunning(data, ctx, now);
}

function applyTerminal(
  data: Payload,
  ctx: FrameApplyContext,
  now: number,
  state: WorkToastState,
): void {
  const key = payloadString(data, "execution_id");
  const store = ctx.workToastStore.getState();
  let item = key ? store.items[key] : undefined;
  // Reconnect can deliver completion before a running frame was observed.
  // Only reconstruct an actionable card when durable conversation identity
  // accompanies the outcome; unknown legacy terminal ids remain ignored.
  if (!item && key && payloadString(data, "conversation_id")) {
    applyRunning(data, ctx, now);
    item = store.items[key] ?? ctx.workToastStore.getState().items[key];
  }
  if (!item?.tracked || isTerminalState(item.state)) return;
  const error =
    state === "failed"
      ? (payloadString(data, "error") ?? payloadString(data, "error_message"))
      : null;
  moveWorkToast(ctx, item, state, now, {
    question: null,
    ...(error ? { detail: error } : {}),
  });
}


function questionOptions(raw: unknown): WorkToastQuestionOption[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    const option = entry as Payload | null;
    if (typeof option?.id !== "string" || typeof option.label !== "string")
      return [];
    return typeof option.description === "string"
      ? [
          {
            id: option.id,
            label: option.label,
            description: option.description,
          },
        ]
      : [{ id: option.id, label: option.label }];
  });
}

function applyQuestion(
  data: Payload,
  ctx: FrameApplyContext,
  now: number,
): void {
  const key = payloadString(data, "execution_id");
  const awaitingInputId = payloadString(data, "awaiting_input_id");
  if (!key || !awaitingInputId) return;
  if (isWorkToastQuestionAnswered(awaitingInputId)) return;
  const store = ctx.workToastStore.getState();
  const item = store.items[key];
  const name = displayAgentName(payloadString(data, "name"));
  const context = payloadString(data, "context");
  // The question names its own project; an item's may be a guess from the
  // project on screen when its run began.
  const projectId = payloadProjectId(data) ?? item?.projectId;
  const identity = name ? { agentName: name } : {};
  if (item?.question?.awaitingInputId === awaitingInputId) {
    store.patchItem(key, {
      ...identity,
      projectId,
      question: { ...item.question, context: context ?? item.question.context },
    });
    return;
  }
  const question = {
    awaitingInputId,
    context,
    options: questionOptions(data.options),
    question: typeof data.question === "string" ? data.question : "",
  };
  if (item) {
    moveWorkToast(ctx, item, "question", now, {
      ...identity,
      projectId,
      question,
    });
    return;
  }
  store.upsertItem(
    createWorkToastItem(
      {
        key,
        source: "agent",
        tracked: false,
        state: "question",
        agentName: name ?? "Agent",
        projectId: projectId ?? currentProjectId(ctx),
        question,
      },
      now,
    ),
  );
}

/** The live toast for an execution: its task card, else its standalone computer card. */
function trackedWorkingItem(ctx: FrameApplyContext, executionId: string): WorkToastItem | null {
  const items = ctx.workToastStore.getState().items;
  const item = items[executionId] ?? items[computerToastKey(executionId)];
  return item?.tracked && item.state === "working" ? item : null;
}

/** D5: a tool call or the reply being written becomes line 2. Frames the
 *  reducer treats as non-events are skipped the same way: an MCP frame-action
 *  cursor (`action_seq` without `tool_use_id`), a failure flag without a
 *  label, non-streaming text and the waking-up status. */
function applyCursorActivity(data: Payload, ctx: FrameApplyContext, now: number): void {
  const executionId = payloadString(data, "execution_id");
  if (!executionId) return;
  const item = trackedWorkingItem(ctx, executionId);
  if (!item) return;
  const toolCall = payloadString(data, "tool_call");
  const toolUseId = payloadString(data, "tool_use_id");
  if (toolCall) {
    if (toolUseId) {
      pushWorkToastActivity(ctx, item.key, {
        id: toolUseId, kind: "tool", action: toolCall, actionLabel: asActivityLabel(data.tool_call_label), updatedAt: now,
      });
    }
    return;
  }
  if (data.tool_failed === true || data.assistant_streaming === false) return;
  const content = payloadString(data, "content_full") ?? payloadString(data, "content");
  if (!content || isCursorStatusContent(content)) return;
  const textFull = stripCursorDisplayTokens(content).trim();
  const text = textActivityFromContent(textFull);
  if (!text) return;
  pushWorkToastActivity(ctx, item.key, {
    id: activityTextId(payloadString(data, "step_id"), item), kind: "text", text, textFull, updatedAt: now,
  });
}

/** Native children stream through message.created: tool rows carry the
 *  backend label, non-final replies are the text being written. */
function applyNativeMessageActivity(data: Payload, metadata: Payload, text: string, ctx: FrameApplyContext, now: number): void {
  const executionId = payloadString(metadata, "execution_id") ?? payloadString(data, "execution_id");
  const nativeId = payloadString(metadata, "native_message_id");
  if (!executionId || !nativeId) return;
  const item = trackedWorkingItem(ctx, executionId);
  if (!item) return;
  if (metadata.activity_kind === "tool_call") {
    const action = payloadString(metadata, "tool_label");
    if (action) {
      pushWorkToastActivity(ctx, item.key, {
        id: nativeId, kind: "tool", action, actionLabel: asActivityLabel(metadata.tool_label_key), updatedAt: now,
      });
    }
    return;
  }
  if (metadata.activity_kind || metadata.native_final === true) return;
  const preview = textActivityFromContent(text);
  if (!preview) return;
  const providerId = payloadString(metadata, "provider_message_id") ?? nativeId;
  pushWorkToastActivity(ctx, item.key, { id: `text:${providerId}`, kind: "text", text: preview, textFull: text, updatedAt: now });
}

function messageText(content: unknown): string {
  if (typeof content === "string") return content;
  const text = (content as { text?: unknown } | null)?.text;
  return typeof text === "string" ? text : "";
}

function applyResultMessage(data: Payload, ctx: FrameApplyContext, now: number): void {
  const metadata = data.metadata as Payload | undefined;
  const text = stripCursorDisplayTokens(messageText(data.content)).trim();
  if (!text) return;
  const store = ctx.workToastStore.getState();
  const source = payloadString(metadata, "source");
  if (source === "worker_result") {
    const key = payloadString(metadata, "worker_execution_id");
    const resultText = stripWorkerResultPrefix(text);
    if (key && store.items[key] && store.items[key].resultText !== resultText)
      store.patchItem(key, { resultText, resultSuperseded: false });
    return;
  }
  if (source === "native_subagent_result") {
    const nativeId = payloadString(metadata, "native_subagent_id");
    const item = nativeId
      ? newestWorkToast(
          store.items,
          (candidate) => candidate.nativeSubagentId === nativeId,
        )
      : null;
    if (item && !item.resultText)
      store.patchItem(item.key, { resultText: stripNativeResultPrefix(text), resultSuperseded: false });
    return;
  }
  if (data.role !== "assistant") return;
  if (metadata?.native_message_id) {
    applyNativeMessageActivity(data, metadata, text, ctx, now);
    if (metadata.native_final !== true) return;
  }
  if (
    metadata?.activity_kind === "tool_call" ||
    metadata?.activity_kind === "progress"
  )
    return;
  const key =
    payloadString(data, "execution_id") ??
    payloadString(metadata, "execution_id");
  const item = key ? store.items[key] : undefined;
  if (!item?.tracked || item.resultText !== null) return;
  store.patchItem(item.key, { resultText: text, resultSuperseded: false });
}

export function applyWorkToastExecutionEvent(
  message: WsMessage,
  ctx: FrameApplyContext,
  now: number,
): boolean {
  const data = message.data;
  switch (message.type) {
    case "execution.status": {
      const status = String(data.status);
      const terminal = TERMINAL_STATE[status];
      if (status === "running") applyRunning(data, ctx, now);
      else if (terminal) applyTerminal(data, ctx, now, terminal);
      return true;
    }
    case "prompt.receipt":
      applyAcceptedPrompt(data, ctx, now);
      return true;

    case "cursor.question":
    case "awaiting_input.requested":
      applyQuestion(data, ctx, now);
      return true;
    case "awaiting_input.resolved": {
      const awaitingInputId = payloadString(data, "awaiting_input_id");
      if (awaitingInputId) resolveWorkToastQuestion(ctx, awaitingInputId, now);
      applyWorkToastQuestionDelivery({ ...data, status: data.answer_delivery_status, error: data.answer_delivery_error }, ctx, now);
      return true;
    }
    case "question.answer_delivery":
      applyWorkToastQuestionDelivery(data, ctx, now);
      return true;
    case "message.created":
      applyResultMessage(data, ctx, now);
      return true;
    case "cursor.move":
      applyCursorActivity(data, ctx, now);
      return true;
    default:
      return false;
  }
}
